<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>
        

        <div class="p-4 sm:p-6">
            <div class="mx-auto max-w-7xl rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                <div class="mb-4 border-b border-gray-200 pb-3">
                    <h1 class="text-xl font-bold text-gray-800 sm:text-2xl">Riconoscimento Documenti</h1>
                    <p class="mt-1 text-sm text-gray-500">
                        Carica un PDF per estrarre i dati dei documenti di trasporto.
                    </p>
                </div>

                <form @submit.prevent="submitForm" class="space-y-3">
                    

                    <div
                        v-if="!isSubmitting"
                        role="button"
                        tabindex="0"
                        class="rounded-lg border-2 border-dashed p-2 text-center transition-colors"
                        :class="[
                            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50',
                            isSubmitting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                        ]"
                        @dragover.prevent="onDragOver"
                        @dragleave.prevent="onDragLeave"
                        @drop.prevent="onDrop"
                        @click="openFilePicker"
                        @keydown.enter.prevent="openFilePicker"
                        @keydown.space.prevent="openFilePicker"
                    >
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".pdf,application/pdf"
                            class="hidden"
                            :disabled="isSubmitting"
                            @click.stop
                            @change="onFileChange"
                        />
                        <v-icon name="bi-file-earmark-arrow-up" scale="1.5" class="mb-1 text-gray-400" />
                        <p class="text-sm font-medium text-gray-700">
                            {{ isDragging ? 'Rilascia il PDF per selezionarlo' : 'Trascina qui il PDF oppure clicca per selezionarlo' }}
                        </p>
                        <p class="mt-1 text-xs text-gray-500">È possibile caricare un file PDF</p>
                        <div v-if="file" class="mt-2 inline-flex max-w-full items-center gap-2 rounded-md border border-blue-200 bg-white px-3 py-1.5 text-left shadow-sm">
                            <v-icon name="bi-file-earmark-pdf" class="shrink-0 text-red-500" />
                            <span class="truncate text-sm font-medium text-gray-800">{{ file.name }}</span>
                            <span class="shrink-0 text-xs text-gray-500">{{ formatFileSize(file.size) }}</span>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <PrimaryButton type="submit" :disabled="isSubmitting || !file">
                            <v-icon v-if="isSubmitting" name="hi-refresh" class="mr-2 animate-spin" />
                            {{ isSubmitting ? 'Elaborazione...' : 'Elabora documento' }}
                        </PrimaryButton>
                    </div>
                </form>

                <div v-if="isSubmitting && jobProgress" class="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h2 class="font-semibold text-gray-800">Avanzamento elaborazione</h2>
                            <p class="mt-1 text-sm text-gray-600">{{ jobStatusLabel }}</p>
                        </div>
                        <span v-if="hasKnownProgress" class="text-sm font-semibold text-blue-800">
                            {{ jobProgressPercentage }}%
                        </span>
                        <v-icon v-else-if="isSubmitting" name="hi-refresh" class="animate-spin text-blue-600" />
                    </div>
                    <div
                        class="h-2.5 overflow-hidden rounded-full bg-blue-100"
                        role="progressbar"
                        :aria-valuenow="hasKnownProgress ? jobProgressPercentage : undefined"
                        :aria-valuemin="0"
                        :aria-valuemax="100"
                        :aria-label="jobStatusLabel"
                    >
                        <div
                            v-if="hasKnownProgress"
                            class="h-full rounded-full bg-blue-600 transition-all duration-500"
                            :style="{ width: `${jobProgressPercentage}%` }"
                        />
                        <div v-else class="h-full w-2/5 rounded-full bg-blue-600 animate-progress-indeterminate" />
                    </div>
                    <p v-if="jobProgress.total_pages > 0" class="mt-1 text-sm text-gray-600">
                        Pagine elaborate: {{ jobProgress.processed_pages }} / {{ jobProgress.total_pages }}
                    </p>
                </div>

                <div v-if="errorMessage" class="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {{ errorMessage }}
                </div>

                
                <p v-else-if="jobProgress?.status === 'completed'" class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                    Nessun documento trovato nel PDF.
                </p>
            </div>
        </div>
        <div v-if="filePreviewUrl || tableDocuments.length" class="mx-auto mt-6 grid w-full grid-cols-1 gap-6 px-6 xl:grid-cols-2">
            <section class="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                <div class="mb-3 flex items-center justify-between gap-3">
                    <h2 class="text-lg font-semibold text-gray-800">Dati estratti</h2>
                    <PrimaryButton v-if="tableDocuments.length" type="button" @click="downloadExcel">Salva modifiche</PrimaryButton>
                </div>
                <div v-if="tableDocuments.length" class="max-h-[70vh] overflow-auto">
                    <PageNormalTable :headers="documentTableHeaders" :items="tableDocuments">
                        <template #item.numero_ddt="{ item, index }">
                            <input v-model="item.numero_ddt" type="text" :aria-label="`Numero DDT riga ${index + 1}`" class="w-32 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.data_ddt="{ item, index }">
                            <input v-model="item.data_ddt" type="text" :aria-label="`Data DDT riga ${index + 1}`" class="w-32 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.mittente="{ item, index }">
                            <input v-model="item.mittente" type="text" :aria-label="`Mittente riga ${index + 1}`" class="w-40 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.destinatario_nome="{ item, index }">
                            <input v-model="item.destinatario_nome" type="text" :aria-label="`Destinatario riga ${index + 1}`" class="w-40 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.destinatario_indirizzo="{ item, index }">
                            <input v-model="item.destinatario_indirizzo" type="text" :aria-label="`Indirizzo riga ${index + 1}`" class="w-48 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.destinatario_localita="{ item, index }">
                            <input v-model="item.destinatario_localita" type="text" :aria-label="`Località riga ${index + 1}`" class="w-40 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.destinatario_provincia="{ item, index }">
                            <input v-model="item.destinatario_provincia" type="text" :aria-label="`Provincia riga ${index + 1}`" class="w-32 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.colli="{ item, index }">
                            <input v-model="item.colli" type="text" :aria-label="`Colli riga ${index + 1}`" class="w-32 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                        <template #item.chili="{ item, index }">
                            <input v-model="item.chili" type="text" :aria-label="`Peso riga ${index + 1}`" class="w-32 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        </template>
                    </PageNormalTable>
                </div>
                <p v-else class="text-sm text-gray-500">I dati estratti appariranno qui al termine dell’elaborazione.</p>
            </section>

            <section v-if="filePreviewUrl" class="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6">
                <h2 class="mb-3 text-lg font-semibold text-gray-800">Visualizzazione PDF</h2>
                <iframe
                    :src="filePreviewUrl"
                    :title="pdfPreviewName || 'PDF caricato'"
                    class="h-[70vh] min-h-[400px] w-full rounded-md border border-gray-200"
                />
            </section>
        </div>


        
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import axios from 'axios';
import ExcelJS from 'exceljs';

export default {
    components: {
        BreadCrumbs,
        PageNormalTable,
        PrimaryButton,
    },
    
    data() {
        return {
            errorMessage: '',
            jobProgress: null,
            editableDocuments: [],
            file: null,
            filePreviewUrl: null,
            pdfPreviewName: '',
            isDragging: false,
            isSubmitting: false,
            isMounted: true,
        };
    },
    mounted() {
        
    },
    beforeUnmount() {
        this.isMounted = false;
        this.revokePdfPreview();
    },
    computed: {
        jobStatusLabel() {
            return {
                queued: 'In attesa di elaborazione',
                processing: 'Elaborazione in corso',
                completed: 'Elaborazione completata',
                failed: 'Elaborazione non riuscita',
            }[this.jobProgress?.status] ?? 'Preparazione elaborazione';
        },
        hasKnownProgress() {
            return Number(this.jobProgress?.total_pages) > 0 || ['completed', 'failed'].includes(this.jobProgress?.status);
        },
        jobProgressPercentage() {
            if (this.jobProgress?.status === 'completed' && Number(this.jobProgress?.total_pages) <= 0) {
                return 100;
            }

            const totalPages = Number(this.jobProgress?.total_pages) || 0;
            const processedPages = Number(this.jobProgress?.processed_pages) || 0;

            return totalPages > 0 ? Math.min(100, Math.round((processedPages / totalPages) * 100)) : 0;
        },
        documentTableHeaders() {
            return [
                { title: 'Numero DDT', key: 'numero_ddt' },
                { title: 'Data DDT', key: 'data_ddt' },
                { title: 'Mittente', key: 'mittente' },
                { title: 'Destinatario', key: 'destinatario_nome' },
                { title: 'Indirizzo', key: 'destinatario_indirizzo' },
                { title: 'Località', key: 'destinatario_localita' },
                { title: 'Provincia', key: 'destinatario_provincia' },
                { title: 'Colli', key: 'colli' },
                { title: 'Peso (kg)', key: 'chili' },
            ];
        },
        tableDocuments() {
            return this.editableDocuments;
        },
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: 'Riconoscimento Documenti AI',
                    href: route('modules.home', 'riconoscimento-documenti-ai'),
                },
                {
                    name: 'Riconoscimento Affidamenti',
                    href: '#',
                }
            ];
        },
    },
    methods: {
        onDragOver() {
            if (!this.isSubmitting) {
                this.isDragging = true;
            }
        },
        onDragLeave(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isDragging = false;
            }
        },
        openFilePicker() {
            if (!this.isSubmitting) {
                this.$refs.fileInput?.click();
            }
        },
        onDrop(event) {
            this.isDragging = false;

            if (!this.isSubmitting) {
                this.setSelectedFile(event.dataTransfer.files?.[0] ?? null);
            }
        },
        onFileChange(event) {
            this.setSelectedFile(event.target.files?.[0] ?? null);
        },
        syncTableDocuments(documents) {
            this.editableDocuments = documents.map((documento, index) => this.editableDocuments[index] ?? ({
                numero_ddt: documento.numero_ddt ?? '',
                data_ddt: documento.data_ddt ?? '',
                mittente: documento.mittente ?? '',
                destinatario_nome: documento.destinatario?.ragione_sociale_destinatario ?? '',
                destinatario_indirizzo: documento.destinatario?.indirizzo_destinatario ?? '',
                destinatario_localita: documento.destinatario?.localita_destinatario ?? '',
                destinatario_provincia: documento.destinatario?.provincia_destinatario ?? '',
                colli: Array.isArray(documento.colli) ? documento.colli.join(', ') : documento.colli ?? '',
                chili: documento.chili ?? '',
            }));
        },
        setSelectedFile(file) {
            if (!file) {
                return;
            }

            this.errorMessage = '';

            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                this.file = null;
                this.jobProgress = null;
                this.editableDocuments = [];
                this.revokePdfPreview();
                this.errorMessage = 'Seleziona un file PDF.';
                if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                return;
            }

            this.revokePdfPreview();
            this.jobProgress = null;
            this.editableDocuments = [];
            this.file = file;
            this.filePreviewUrl = URL.createObjectURL(file);
            this.pdfPreviewName = file.name;
        },
        revokePdfPreview() {
            if (this.filePreviewUrl) {
                URL.revokeObjectURL(this.filePreviewUrl);
                this.filePreviewUrl = null;
            }

            this.pdfPreviewName = '';
        },
        formatFileSize(size) {
            if (size < 1024 * 1024) {
                return `${Math.max(1, Math.round(size / 1024))} KB`;
            }

            return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        },
        async downloadExcel() {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Dati estratti');
            const headers = this.documentTableHeaders;

            worksheet.addRow(headers.map((header) => header.title));
            this.tableDocuments.forEach((document) => {
                worksheet.addRow(headers.map((header) => document[header.key] ?? ''));
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const baseName = this.pdfPreviewName.replace(/\\.pdf$/i, '').replace(/[\\\\/:*?"<>|]/g, '_') || 'dati-estratti';

            link.href = downloadUrl;
            link.download = `${baseName}.xlsx`;
            link.click();
            URL.revokeObjectURL(downloadUrl);
        },

        async submitForm() {
            if (this.isSubmitting) {
                return;
            }

            if (!this.file) {
                this.errorMessage = 'Seleziona un documento PDF.';
                return;
            }

            this.errorMessage = '';
            this.jobProgress = null;
            this.editableDocuments = [];
            this.isSubmitting = true;

            try {
                const formData = new FormData();
                formData.append('document', this.file);

                const response = await axios.post(route('modules.gemini.chat'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                await this.pollJob(response.data.job_id);
            } catch (error) {
                if (this.isMounted) {
                    this.errorMessage = error.response?.data?.message ?? "Errore durante l'invio del documento.";
                }
            } finally {
                if (this.isMounted) {
                    this.isSubmitting = false;
                    this.file = null;
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                }
            }
        },

        async pollJob(jobId) {
            while (this.isMounted) {
                const response = await axios.get(route('modules.gemini.chat.status', { jobId }));
                this.jobProgress = response.data;
                this.syncTableDocuments(response.data.documenti ?? []);

                if (response.data.status === 'completed') {
                    return;
                }

                if (response.data.status === 'failed') {
                    this.errorMessage = response.data.message;
                    return;
                }

                await new Promise((resolve) => window.setTimeout(resolve, 2000));
            }
        },
       
    },
};
</script>

<style scoped>
@keyframes progress-indeterminate {
    from {
        transform: translateX(-100%);
    }
    to {
        transform: translateX(250%);
    }
}

.animate-progress-indeterminate {
    animation: progress-indeterminate 1.5s ease-in-out infinite;
}
</style>
